import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef } from 'react';
import { useApi } from '../hooks/useApi';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { API_ENDPOINTS } from '../constants';

// API レスポンス型定義
interface AuthResponse {
  user?: User;
  token?: string;
  authentication?: {
    access_token?: string;
    token?: string;
    accessToken?: string;
  };
}

interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  display_name: string;
  is_staff: boolean;
  is_superuser: boolean;
  organization?: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
}

interface LoginData {
  username: string;
  password: string;
}

interface RegisterData {
  username: string;
  email: string;
  password: string;
  first_name?: string;
  last_name?: string;
}

interface AuthContextType extends AuthState {
  login: (credentials: LoginData) => Promise<boolean>;
  register: (userData: RegisterData) => Promise<boolean>;
  logout: () => Promise<void>;
  validateToken: () => Promise<void>;
  isAdmin: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { execute: apiCall } = useApi({ credentials: 'include' }); // Cookie認証を有効化
  const [token, setToken, removeToken] = useLocalStorage<string | null>('authToken', null);
  
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    loading: true,
  });

  // StrictMode対応 - 重複実行防止
  const isValidating = useRef(false);

  // トークンの有効性確認（StrictMode対応版）
  const validateToken = useCallback(async () => {
    // 既に検証中の場合はスキップ
    if (isValidating.current) {
      if (process.env.NODE_ENV === 'development') {
        console.log('🚫 トークン検証は既に実行中です');
      }
      return;
    }

    isValidating.current = true; // 実行フラグを設定
    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 トークン検証開始');
    }
    
    try {
      // 1. まずCookie認証を試行（HttpOnly Cookie）
      let userData = await apiCall(API_ENDPOINTS.USER_PROFILE, {
        credentials: 'include', // Cookie認証を有効化
      });

      // 2. Cookie認証が失敗した場合、JWTヘッダーを試行
      if (!userData && token) {
        if (process.env.NODE_ENV === 'development') {
          console.log('🔄 Cookie認証失敗、JWTヘッダー認証を試行');
        }
        userData = await apiCall(API_ENDPOINTS.USER_PROFILE, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      

      if (userData && (userData as User).id) {
        setAuthState({
          user: userData as User,
          isAuthenticated: true,
          loading: false,
        });

      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log('AuthContext - Invalid token response, removing token');
        }
        removeToken();
        setAuthState({ user: null, isAuthenticated: false, loading: false });
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('AuthContext - Token validation failed:', error);
      }
      removeToken();
      setAuthState({ user: null, isAuthenticated: false, loading: false });
    } finally {
      isValidating.current = false; // 実行フラグをリセット
      if (process.env.NODE_ENV === 'development') {
        console.log('🏁 トークン検証完了 - 実行フラグリセット');
      }
    }
  }, [token, apiCall, removeToken]);

  // ログイン
  const login = useCallback(async (credentials: LoginData): Promise<boolean> => {
    setAuthState(prev => ({ ...prev, loading: true }));

    try {
  
      const response = await apiCall(API_ENDPOINTS.LOGIN, {
        method: 'POST',
        // 送信前に前後の空白を除去（コピー&ペースト事故対策）
        body: JSON.stringify({
          username: credentials.username.trim(),
          password: credentials.password.trim(),
        }),
        credentials: 'include', // Cookieを確実に受け取る
      });

      
      // 新形式: {user: {...}, authentication: {access_token: ...}}
      const authResponse = response as AuthResponse;
      if (response && authResponse.authentication && authResponse.user) {
        const token = authResponse.authentication.access_token || 
                     authResponse.authentication.token || 
                     authResponse.authentication.accessToken;
        
        if (token) {
          setToken(token);
          setAuthState({
            user: authResponse.user,
            isAuthenticated: true,
            loading: false,
          });
  
          return true;
        }
      } 
      // レガシー形式: {user: {...}, token: ...}
      else if (response && authResponse.token && authResponse.user) {
        setToken(authResponse.token);
        setAuthState({
          user: authResponse.user,
          isAuthenticated: true,
          loading: false,
        });

        return true;
      }
      

      setAuthState(prev => ({ ...prev, loading: false }));
      return false;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('AuthContext - Login failed:', error);
      }
      setAuthState(prev => ({ ...prev, loading: false }));
      return false;
    }
  }, [apiCall, setToken]);

  // ユーザー登録
  const register = useCallback(async (userData: RegisterData): Promise<boolean> => {
    setAuthState(prev => ({ ...prev, loading: true }));

    try {
      const response = await apiCall(API_ENDPOINTS.REGISTER, {
        method: 'POST',
        body: JSON.stringify(userData),
      });

      const authResponse = response as AuthResponse;
      if (response && authResponse.token && authResponse.user) {
        setToken(authResponse.token);
        setAuthState({
          user: authResponse.user,
          isAuthenticated: true,
          loading: false,
        });
        return true;
      }
      return false;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('AuthContext - Registration failed:', error);
      }
      setAuthState(prev => ({ ...prev, loading: false }));
      return false;
    }
  }, [apiCall, setToken]);

  // ログアウト
  const logout = useCallback(async () => {
    try {
      if (token) {
        await apiCall(API_ENDPOINTS.LOGOUT, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('AuthContext - Logout API call failed:', error);
      }
    } finally {
      removeToken();
      setAuthState({ user: null, isAuthenticated: false, loading: false });
  
    }
  }, [token, apiCall, removeToken]);

  // 管理者権限チェック
  const isAdmin = useCallback(() => {
    return authState.user?.is_staff || authState.user?.is_superuser || false;
  }, [authState.user]);

  // 初期化時のトークン検証（StrictMode対応版）
  useEffect(() => {
    let isCancelled = false; // クリーンアップフラグ

    const initializeAuth = async () => {
      if (isCancelled) return; // キャンセルされている場合は実行しない
      await validateToken();
    };

    initializeAuth();

    // クリーンアップ関数
    return () => {
      isCancelled = true;
      if (process.env.NODE_ENV === 'development') {
        console.log('🧹 AuthContext useEffect: クリーンアップ実行');
      }
    };
  }, []); // 依存関係を空にして初回のみ実行

  const contextValue: AuthContextType = {
    user: authState.user,
    isAuthenticated: authState.isAuthenticated,
    loading: authState.loading,
    login,
    register,
    logout,
    validateToken,
    isAdmin,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};